import React from "react";
import { isEmail, isMinLength, isPhone } from "../../../utils/forms/validators";
import DataTableX from "../../theme/tables/DataTableX";
import BaseModal from "../../theme/modals/BaseModal";
import BaseForm from "../../theme/forms/BaseForm";
import CampaignMembersTable from "../../admin/CampaignMembersTable";
import api from "../../../store/api/api";
import { UserAccount, Campaign, CampaignMembership } from "../../../types/admin";
import { FormInput } from "../../../types/utils";
import { pullCampaigns } from "../../../store/api/calls";
import { Dialog, DialogTitle, Typography, IconButton, Grid, TextField, MenuItem, Button } from "@mui/material";
import MfaSelector from "./MfaSelector";

interface UsersTabProps {
    campaigns: Campaign[];
    setError: (message: string) => void;
    showSuccess: (message: string) => void;
}

interface UsersTabState {
    editModalOpen: boolean;
    editModalErrorMsg: string | null;
    deleteModalOpen: boolean;
    reactivateModalOpen: boolean;
    editingInstance: Partial<UserAccount>;
    deactivatedUserView: boolean;
    formInputs: any[];
    permissionsModalOpen: boolean;
    users: UserAccount[];
    deactivatedUsers: UserAccount[];
    roles: CampaignMembership[];
    loading: boolean;
    campaignFilter: number;
}

class UsersTab extends React.Component<UsersTabProps, UsersTabState> {
    constructor(props: UsersTabProps) {
        super(props);
        this.state = {
            editModalOpen: false,
            editModalErrorMsg: null,
            deleteModalOpen: false,
            reactivateModalOpen: false,
            editingInstance: {},
            deactivatedUserView: false,
            formInputs: [],
            permissionsModalOpen: false,
            users: [],
            deactivatedUsers: [],
            roles: [],
            loading: false,
            campaignFilter: 0
        };
    }

    componentDidMount() {
        this.fetchUsers();
    }

    fetchUsers = async (): Promise<void> => {
        this.setState({ loading: true });
        try {
            const response = await api.get('/users/?memberships=1', { deactivated: this.state.deactivatedUserView ? 1 : 0 });
            if (this.state.deactivatedUserView) {
                this.setState({ deactivatedUsers: response.data });
            } else {
                this.setState({ users: response.data });
            }
        } catch (error) {
            this.props.setError("Failed to fetch users");
        } finally {
            this.setState({ loading: false });
        }
    };

    // Helper function to format error messages
    formatErrorMessages = (errors: any): string => {
        if (!errors) return "An unknown error occurred";

        // Handle case where errors is an object with field-specific messages
        if (typeof errors === 'object') {
            const messages: string[] = [];
            Object.entries(errors).forEach(([field, fieldErrors]) => {
                if (Array.isArray(fieldErrors)) {
                    messages.push(`${field}: ${fieldErrors.join(', ')}`);
                } else if (typeof fieldErrors === 'string') {
                    messages.push(`${field}: ${fieldErrors}`);
                }
            });
            return messages.join('\n');
        }

        // Handle case where errors is a string
        return errors;
    };

    setMfaRule = (user: UserAccount, prop: string) => {
        let payload = { mfa_enabled: 0, mfa_required: 0 }
        if (prop === 'mfa_required') {
            payload.mfa_required = 1;
            payload.mfa_enabled = 1;
        } else if (prop === 'mfa_enabled') {
            payload.mfa_enabled = 1;
            payload.mfa_required = 0;
        }
        if (!user.profile) {
            return api.post(`/profiles/?user=${user.id}`, payload)
                .then((res) => {
                    console.log(res.data);
                    this.fetchUsers();
                }).catch((err) => {
                    console.log(err);
                })
        } else {
            const profile = user.profile;
            return api.patch(`/profiles/${profile.id}/`, payload)
                .then((res) => {
                    console.log(res.data);
                    this.fetchUsers();
                }).catch((err) => {
                    console.log(err);
                })
        }
    };

    buildTable() {
        const columns = [{
            "field": "id",
            "headerName": "Actions",
            "type": "number",
            "sortable": false,
            "filterable": false,
            "renderCell": ({ row }) => {
                return <Grid container spacing={2}>
                    <Grid item>
                        <div onClick={() => this.onOpenEditModal(row)}>
                            <i className="fa fa-edit" />
                        </div>
                    </Grid>
                    <Grid item>
                        <div onClick={() => this.openUserStatusModal(row)}>
                            <i className={`fa ${row.is_active ? 'fa-minus-square' : 'fa-plus-square'}`} />
                        </div>
                    </Grid>
                    <Grid item>
                        <div onClick={() => this.onOpenPermissionsModal(row)}>
                            <i className="fa fa-cog" />
                        </div>
                    </Grid>
                </Grid>
            }
        }, {
            "field": "username",
            "headerName": "Username",
            "type": "string",
            "sortable": true,
            "filterable": true
        },
        {
            "field": "first_name",
            "headerName": "Full Name",
            "type": "string",
            "sortable": true,
            "filterable": true,
            "renderCell": function ({ row }) {
                return `${row.first_name} ${row.last_name}`;
            }
        },
        {
            "field": "is_active",
            "headerName": "MFA",
            "type": "boolean",
            "sortable": true,
            "filterable": false,
            "renderCell": ({ row }) => {
                return <MfaSelector key={row.id + 'mfaselector'} user={row} />
            },
            "valueGetter": function ({ row }) {
                return (row.profile?.mfa_enabled || 0) + (row.profile?.mfa_required || 0);
            }
        },
        {
            "field": "date_joined",
            "headerName": "Date Joined",
            "type": "time",
            "sortable": true,
            "filterable": false,
            "renderCell": function ({ row }) {
                return new Date(row.date_joined).toDateString();
            },
            "valueGetter": function ({ row }) {
                return new Date(row.date_joined).getTime();
            }
        },
        {
            "field": "memberships",
            "headerName": "Memberships",
            "showing": false,
            "type": "string",
            "sortable": false,
            "filterable": false,
            "renderCell": ({ row }) => {
                if (!row.memberships) return null;
                const memberships = this.state.deactivatedUserView ? row.memberships : row.memberships.filter((membership: CampaignMembership) => membership.campaign.is_active === true);
                return memberships.map((membership: CampaignMembership) => membership.campaign.name).join(', ');
            }
        }]


        let users = this.state.deactivatedUserView ? this.state.deactivatedUsers : this.state.users;
        if (this.state.campaignFilter > 0) {
            users = users.filter((user: UserAccount) => user.memberships && user.memberships.some((membership: CampaignMembership) => membership.campaign.id === this.state.campaignFilter));
        }

        return (
            <DataTableX
                showFooter={false}
                displayFilter={false}
                rows={users}
                title={"campaign members"}
                columns={columns}
            />
        );
    }

    _generateFormInputs = (modelInstance: Partial<UserAccount>, creating: boolean = false): FormInput[] => {
        return [
            {
                type: "text",
                title: "First Name",
                dataKey: "first_name",
                placeholder: "Jane",
                validator: isMinLength(1),
                value: modelInstance?.first_name,
                errorMsg: "This field is required",
            },
            {
                type: "text",
                title: "Last Name",
                dataKey: "last_name",
                placeholder: "Smith",
                validator: isMinLength(1),
                value: modelInstance?.last_name,
                errorMsg: "This field is required",
            },
            {
                type: "text",
                title: "Username",
                dataKey: "username",
                placeholder: "Make you sure you have a system for this...",
                validator: isMinLength(1),
                value: modelInstance?.username,
                errorMsg: "This field is required",
            },
            {
                type: "text",
                title: "Email",
                dataKey: "email",
                placeholder: "Make you sure you have a system for this...",
                validator: isEmail,
                value: modelInstance?.email,
                errorMsg: "This must be a valid email",
            },
            {
                type: "text",
                title: "Phone Number",
                dataKey: "cell_phone",
                placeholder: "(555) 555-5555",
                validator: isPhone,
                value: modelInstance?.profile?.cell_phone ?? "",
                errorMsg: "Enter a valid 10-digit phone number",
            },
            {
                type: "text",
                title: "Password",
                dataKey: "password",
                placeholder: creating
                    ? "At least five characters, with at least one number and letter"
                    : "Leave blank if you don't want to change this.",
                validator: creating ? isMinLength(1) : () => true,
                errorMsg: "This field is required",
            },
        ];
    };

    onOpenEditModal = (modelInstance: Partial<UserAccount> = {}, creating: boolean = false) => {
        this.setState({
            formInputs: this._generateFormInputs(modelInstance, creating),
            editModalOpen: true,
            editingInstance: modelInstance,
        });
    };

    onCloseEditModal = () => {
        this.setState({
            editModalOpen: false,
            editModalErrorMsg: null,
        });
    };

    openUserStatusModal = (modelInstance: UserAccount) => {
        this.setState({ deleteModalOpen: true, editingInstance: modelInstance });
    };

    closeUserStatusModal = () => {
        this.setState({ deleteModalOpen: false, editingInstance: {} });
    };

    refreshUserPermissions = (uid: number) => {
        api.get(`/users/${uid}/?memberships=1`)
            .then(response => {
                console.log(response.data);
                this.setState({
                    roles: response.data.memberships,
                    permissionsModalOpen: true,
                    editingInstance: response.data,
                });
            })
            .catch(() => {
                this.props.setError("Failed to fetch roles");
            });
    }

    onOpenPermissionsModal = (modelInstance: UserAccount) => {
        this.refreshUserPermissions(modelInstance.id);
    };

    onClosePermissionsModal = () => {
        this.setState({
            permissionsModalOpen: false,
            editingInstance: {},
        });
    };

    handleUpdateCreateSubmission = (data: any) => {
        const handleError = (err: any, fallbackMessage: string) => {
            const errors = err.response?.data?.errors;
            if (errors) {
                this.props.setError(this.formatErrorMessages(errors));
            } else {
                this.props.setError(fallbackMessage);
            }
            throw err;
        };

        if (this.state.editingInstance.id) {
            // Editing existing user - use PATCH
            return api.patch(`/users/${this.state.editingInstance.id}/?memberships=1`, data)
                .then((response) => {
                    this.onCloseEditModal();
                    this.fetchUsers();
                    this.onOpenPermissionsModal(response.data);
                })
                .catch((err) => handleError(err, "Failed to update user"));
        }

        // Creating new user - use POST
        return api.post('/users/?memberships=1', data)
            .then((response) => {
                this.onCloseEditModal();
                this.fetchUsers();
                this.onOpenPermissionsModal(response.data);
            })
            .catch((err) => handleError(err, "Failed to create user"));
    };

    toggleUserStatus = () => {
        api.patch(`/users/${this.state.editingInstance.id}/?memberships=1`, { is_active: !this.state.editingInstance.is_active })
            .then(() => {
                this.closeUserStatusModal();
                this.fetchUsers();
            })
            .catch((err) => {
                const errors = err.response?.data?.errors;
                if (errors) {
                    const errorMsg = this.formatErrorMessages(errors);
                    this.props.setError(errorMsg);
                } else {
                    this.props.setError("Failed to deactivate user");
                }
            });
    };

    toggleUserView = () => {
        this.setState({ deactivatedUserView: !this.state.deactivatedUserView }, () => {
            this.fetchUsers();
        });
    };

    render() {
        const { deactivatedUserView, editingInstance } = this.state;

        return (
            <div id="UsersTab">
                {this.state.loading && <div className="loading-overlay"></div>}
                <Grid container spacing={2} sx={{ marginBottom: '10px' }}>
                    <Grid item>
                        <Button
                            onClick={() => this.onOpenEditModal({}, true)}
                            startIcon={<i className="fa fa-plus" />}
                            disableElevation={true}
                            variant="contained"
                            color="secondary"
                        >
                            Add User
                        </Button>
                    </Grid>
                    <Grid item>
                        {!deactivatedUserView ?
                            <Button
                                variant="contained"
                                color="secondary"
                                disableElevation={true}
                                startIcon={<i className="fas fa-minus-square" />}
                                onClick={this.toggleUserView}>
                                Show Inactive Users
                            </Button>
                            :
                            <Button
                                variant="contained"
                                color="secondary"
                                disableElevation={true}
                                startIcon={<i className="fas fa-plus-square" />}
                                onClick={this.toggleUserView}>
                                Show Active Users
                            </Button>
                        }
                    </Grid>
                    {this.props.campaigns && 
                        <Grid item style={{ flexGrow: 1 }}>
                            <TextField select fullWidth
                                label='Filter by Campaign'
                                size="small"
                                variant="outlined"
                                value={this.state.campaignFilter}
                                onChange={(e) => this.setState({ campaignFilter: parseInt(e.target.value) })}>
                                <MenuItem value={0}>All Campaigns</MenuItem>
                                {this.props.campaigns.map((campaign: Campaign) => (
                                    <MenuItem key={campaign.id} value={campaign.id}>{campaign.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    }
                </Grid>
                {this.buildTable()}

                <BaseModal
                    open={this.state.editModalOpen}
                    onClose={this.onCloseEditModal}
                    center
                >
                    <BaseForm
                        inputs={this.state.formInputs}
                        onCancel={this.onCloseEditModal}
                        onSubmit={this.handleUpdateCreateSubmission}
                    />
                    {!!this.state.editModalErrorMsg && (
                        <div className="danger-text flx center">
                            {this.state.editModalErrorMsg}
                        </div>
                    )}
                </BaseModal>

                <BaseModal
                    open={this.state.deleteModalOpen}
                    onClose={this.closeUserStatusModal}
                    center
                >
                    <div className="modal-internal-wrapper">
                        <p>Are you sure you want to {editingInstance.is_active ? 'deactivate' : 'reactivate'} {editingInstance.first_name} {" "} {editingInstance.last_name}?</p>
                        <div className="button-wrapper">
                            <div className="button" onClick={this.toggleUserStatus}>
                                {editingInstance.is_active ? "Reactivate" : "Remove"}
                            </div>
                            <div className="button button-outline" onClick={this.closeUserStatusModal}>
                                Cancel
                            </div>
                        </div>
                    </div>
                </BaseModal>

                <Dialog
                    open={this.state.permissionsModalOpen}
                    onClose={this.onClosePermissionsModal}
                    fullWidth
                    maxWidth="md"
                >

                    <DialogTitle sx={{padding:1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h4" color="primary">
                            Campaign Memberships for <b>{editingInstance.first_name} {editingInstance.last_name}</b>
                        </Typography>
                        <IconButton onClick={this.onClosePermissionsModal}>
                            <i className="fa fa-times"></i>
                        </IconButton>
                    </DialogTitle>

                    <CampaignMembersTable
                        originalMemberships={this.state.roles}
                        user={editingInstance as UserAccount}
                        refreshUserPermissions={(uid: number) => this.refreshUserPermissions(uid)}
                    />
                </Dialog>

            </div >
        );
    }
}

export default UsersTab; 